import { formatNumberValue, type NumberFormat } from "../../../lib/localeFormat";

export type DocumentKind = "quote" | "order" | "invoice";

export interface DocumentElementConfig {
  label?: string;
  field_key?: string;
  text?: string;
  description?: string;
  quantity?: number | string;
  unit_price?: number | string;
  amount?: number | string;
  rate?: number | string;
  op?: "percent" | "fixed";
  base?: "subtotal" | "total" | "tax_total" | "discount_total";
  apply_as?: "discount" | "tax" | "surcharge" | "display";
  discount_pct?: number | string;
}

export interface DocumentElement {
  id: string;
  type: string;
  col_start: number;
  col_span: number;
  canvas_row: number;
  config?: DocumentElementConfig;
}

export interface DocumentSample {
  kind: string;
  seller_name: string;
  seller_address: string;
  seller_address_lines?: string[];
  seller_tax_number?: string;
  customer_name: string;
  customer_address: string;
  customer_address_lines?: string[];
  number: string;
  issue_date: string;
  expiry_date: string;
  status: string;
  currency: string;
  terms: string;
  notes: string;
  line_items: Array<{
    description: string;
    quantity: number | string;
    unit_price: number | string;
    discount_pct?: number | string;
    template?: boolean;
    amount?: string;
  }>;
  subtotal: number | string;
  discount_total: number | string;
  tax_total: number | string;
  tax_rate?: number | string | null;
  total: number | string;
  template_lines?: Array<Record<string, string | boolean | number>>;
  calc_rows?: Array<{ label: string; amount: string; apply_as: string }>;
}

export const UNIQUE_BLOCK_TYPES = new Set(["logo", "company", "customer", "number", "lines", "totals", "terms", "signature"]);

export const BLOCK_PALETTE = [
  { type: "logo", label: "Logo", description: "Company logo at the top of quotes, orders, and invoices" },
  { type: "company", label: "Our company", description: "Seller name and address" },
  { type: "customer", label: "Customer", description: "Bill-to name and address" },
  { type: "number", label: "Document number", description: "Number, dates and status" },
  { type: "lines", label: "Line items", description: "The product / service table" },
  { type: "totals", label: "Totals", description: "Subtotal, discount, tax and total" },
  { type: "terms", label: "Terms", description: "Payment and validity notes" },
  { type: "signature", label: "Signature", description: "Sign-off line" },
  { type: "text", label: "Text box", description: "Free text on the page" },
] as const;

export const CALC_PALETTE = [
  { preset: "discount_percent", type: "calc", label: "Percent discount", description: "e.g. 20% off the invoice subtotal" },
  { preset: "tax_percent", type: "calc", label: "Percent tax", description: "Tax as a percent of subtotal" },
  { preset: "delivery_fixed", type: "extra_line", label: "Delivery line", description: "Add freight as a line item" },
  { preset: "delivery_percent", type: "extra_line", label: "Delivery (% of subtotal)", description: "Freight as a percent of goods" },
] as const;

export const FIELD_GROUP_ORDER = ["seller", "customer", "document", "amounts"] as const;
export const FIELD_GROUP_LABELS: Record<string, string> = {
  seller: "Our company",
  customer: "Customer",
  document: "Document",
  amounts: "Amounts",
};

export const DOCUMENT_FIELDS = [
  { key: "seller_name", label: "Our company name", kind: "text", group: "seller" },
  { key: "seller_address", label: "Our address", kind: "text", group: "seller" },
  { key: "seller_tax_number", label: "Tax number", kind: "text", group: "seller" },
  { key: "customer_name", label: "Customer name", kind: "text", group: "customer" },
  { key: "customer_address", label: "Customer address", kind: "text", group: "customer" },
  { key: "number", label: "Document number", kind: "text", group: "document" },
  { key: "issue_date", label: "Issue date", kind: "date", group: "document" },
  { key: "expiry_date", label: "Valid until", kind: "date", group: "document" },
  { key: "status", label: "Status", kind: "text", group: "document" },
  { key: "terms", label: "Terms", kind: "text", group: "document" },
  { key: "notes", label: "Notes", kind: "text", group: "document" },
  { key: "currency", label: "Currency", kind: "text", group: "amounts" },
  { key: "subtotal", label: "Subtotal", kind: "currency", group: "amounts" },
  { key: "discount_total", label: "Discount total", kind: "currency", group: "amounts" },
  { key: "tax_total", label: "Tax total", kind: "currency", group: "amounts" },
  { key: "total", label: "Total", kind: "currency", group: "amounts" },
] as const;

export const MONEY_FIELDS = new Set(["subtotal", "discount_total", "tax_total", "total"]);

const DEFAULT_SPANS: Record<string, { col_start: number; col_span: number }> = {
  logo: { col_start: 1, col_span: 3 },
  company: { col_start: 1, col_span: 3 },
  customer: { col_start: 5, col_span: 3 },
  number: { col_start: 5, col_span: 3 },
  lines: { col_start: 1, col_span: 7 },
  totals: { col_start: 5, col_span: 3 },
  terms: { col_start: 1, col_span: 7 },
  signature: { col_start: 5, col_span: 3 },
  extra_line: { col_start: 1, col_span: 7 },
  calc: { col_start: 5, col_span: 3 },
  field: { col_start: 1, col_span: 3 },
  text: { col_start: 1, col_span: 7 },
};

let counter = 0;
export function newElementId(prefix: string): string {
  counter += 1;
  return `${prefix}_${Date.now()}_${counter}`;
}

export function samplePayload(kind: string): DocumentSample {
  const number = kind === "invoice" ? "INV-DEMO-0001" : kind === "order" ? "O-DEMO-0001" : "Q-DEMO-0001";
  return {
    kind,
    seller_name: "Demo Organization",
    seller_address: "107 Sunny Crescent\nAustin\nTexas\n4000",
    seller_address_lines: ["107 Sunny Crescent", "Austin", "Texas", "4000"],
    seller_tax_number: "",
    customer_name: "Apex Industries",
    customer_address: "Austin, US",
    customer_address_lines: ["Austin", "US"],
    number,
    issue_date: "2026-09-14",
    expiry_date: "2026-10-14",
    status: kind === "quote" ? "sent" : "draft",
    currency: "USD",
    terms: "Sample implementation quote",
    notes: "",
    line_items: [{ description: "Implementation package", quantity: 1, unit_price: 8500, discount_pct: 0 }],
    subtotal: 8500,
    discount_total: 0,
    tax_total: 0,
    total: 8500,
  };
}

function num(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function lineAmount(item: { quantity?: unknown; unit_price?: unknown; discount_pct?: unknown }): number {
  const raw = num(item.quantity) * num(item.unit_price);
  return raw - raw * (num(item.discount_pct) / 100);
}

export function applyTemplateEffects(elements: DocumentElement[], payload: DocumentSample): DocumentSample {
  const original = (payload.line_items ?? []).filter((item) => !item.template);
  let baseSubtotal = original.reduce((sum, item) => sum + lineAmount(item), 0);
  if (!baseSubtotal) baseSubtotal = num(payload.subtotal);

  const templateLines: DocumentSample["line_items"] = [];
  const items = [...original];
  for (const element of elements) {
    if (element.type !== "extra_line") continue;
    const cfg = element.config ?? {};
    const qty = num(cfg.quantity, 1) || 1;
    const op = cfg.op === "percent" ? "percent" : "fixed";
    const unit = op === "percent" ? (baseSubtotal * num(cfg.rate)) / 100 / qty : num(cfg.unit_price ?? cfg.amount);
    const line = {
      description: cfg.description || cfg.label || "Item",
      quantity: qty,
      unit_price: unit,
      discount_pct: num(cfg.discount_pct),
      template: true,
    };
    items.push(line);
    templateLines.push({ ...line, amount: String(lineAmount(line)) });
  }

  const subtotal = items.reduce((sum, item) => sum + lineAmount(item), 0);
  let discount = num(payload.discount_total);
  let tax = num(payload.tax_total);
  let surcharge = 0;
  const calcRows: NonNullable<DocumentSample["calc_rows"]> = [];
  for (const element of elements) {
    if (element.type !== "calc") continue;
    const cfg = element.config ?? {};
    const amounts: Record<string, number> = {
      subtotal,
      discount_total: discount,
      tax_total: tax,
      total: subtotal - discount + tax,
    };
    const value = cfg.op === "fixed"
      ? num(cfg.amount)
      : amounts[cfg.base && cfg.base in amounts ? cfg.base : "subtotal"] * num(cfg.rate) / 100;
    const applyAs = cfg.apply_as === "discount" || cfg.apply_as === "tax" || cfg.apply_as === "surcharge" ? cfg.apply_as : "display";
    if (applyAs === "discount") discount += value;
    else if (applyAs === "tax") tax += value;
    else if (applyAs === "surcharge") surcharge += value;
    calcRows.push({ label: cfg.label || "Calculation", amount: String(value), apply_as: applyAs });
  }

  const next = { ...payload, line_items: items, template_lines: templateLines, calc_rows: calcRows };
  if (templateLines.length || calcRows.length) {
    next.subtotal = subtotal;
    next.discount_total = discount;
    next.tax_total = tax;
    next.total = subtotal - discount + tax + surcharge;
  }
  return next;
}

export function money(value: unknown, currency = "", numberFormat?: NumberFormat): string {
  const n = num(value);
  const formatted = numberFormat
    ? formatNumberValue(n, { numberFormat, fractionDigits: 2 })
    : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${formatted} ${currency}` : formatted;
}

export function defaultSpan(type: string): { col_start: number; col_span: number } {
  return DEFAULT_SPANS[type] ?? { col_start: 1, col_span: 7 };
}

export function maxRow(elements: DocumentElement[]): number {
  return elements.reduce((max, element) => Math.max(max, element.canvas_row || 1), 0);
}

export function insertRow(elements: DocumentElement[], row: number): DocumentElement[] {
  return elements.map((element) => (element.canvas_row >= row ? { ...element, canvas_row: element.canvas_row + 1 } : element));
}

export function elementFromPalette(type: string, kind: string, row: number): DocumentElement {
  const span = defaultSpan(type);
  const label = BLOCK_PALETTE.find((item) => item.type === type)?.label
    ?? (type === "number" ? `${kind.charAt(0).toUpperCase()}${kind.slice(1)} number` : type);
  return {
    id: newElementId(type),
    type,
    canvas_row: row,
    col_start: span.col_start,
    col_span: span.col_span,
    config: { label },
  };
}

export function elementFromPreset(preset: string, row: number): DocumentElement {
  if (preset === "tax_percent") {
    const span = defaultSpan("calc");
    return {
      id: newElementId("calc"),
      type: "calc",
      canvas_row: row,
      ...span,
      config: { label: "Tax 10%", op: "percent", base: "subtotal", rate: 10, apply_as: "tax" },
    };
  }
  if (preset === "delivery_fixed") {
    const span = defaultSpan("extra_line");
    return {
      id: newElementId("extra_line"),
      type: "extra_line",
      canvas_row: row,
      ...span,
      config: { label: "Delivery", description: "Delivery", quantity: 1, unit_price: 75, op: "fixed" },
    };
  }
  if (preset === "delivery_percent") {
    const span = defaultSpan("extra_line");
    return {
      id: newElementId("extra_line"),
      type: "extra_line",
      canvas_row: row,
      ...span,
      config: { label: "Delivery", description: "Delivery", quantity: 1, op: "percent", rate: 5, apply_as: "surcharge" },
    };
  }
  const span = defaultSpan("calc");
  return {
    id: newElementId("calc"),
    type: "calc",
    canvas_row: row,
    ...span,
    config: { label: "Discount 20%", op: "percent", base: "subtotal", rate: 20, apply_as: "discount" },
  };
}

export function elementFromField(fieldKey: string, row: number): DocumentElement {
  const field = DOCUMENT_FIELDS.find((item) => item.key === fieldKey);
  const span = defaultSpan("field");
  return {
    id: newElementId("field"),
    type: "field",
    canvas_row: row,
    ...span,
    config: { label: field?.label ?? fieldKey, field_key: fieldKey },
  };
}

export function placementStyle(element: DocumentElement): { gridColumn: string; gridRow: number } {
  const start = Math.max(1, Math.min(element.col_start || 1, 7));
  const span = Math.max(1, Math.min(element.col_span || 1, 8 - start));
  return { gridColumn: `${start} / span ${span}`, gridRow: Math.max(1, element.canvas_row || 1) };
}

export function blockLabel(element: DocumentElement): string {
  return element.config?.label || BLOCK_PALETTE.find((item) => item.type === element.type)?.label || element.type;
}
